import * as XLSX from 'xlsx';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const fmt = (v) => (v == null || v === '' ? '' : v);

const sortByCheckoutThenCode = (a, b) => {
  const da = String(a.check_out_date || a.check_in_date || '');
  const db = String(b.check_out_date || b.check_in_date || '');
  if (da !== db) return da.localeCompare(db);
  return String(a.booking_code || a.booking_id || '').localeCompare(
    String(b.booking_code || b.booking_id || '')
  );
};

/** Who files room GST for this booking */
const roomGstFiledByLabel = (b) => {
  const section = String(b.section || '').toLowerCase();
  const filed = String(b.room_gst_filed_by || '').toLowerCase();
  const sendToHotel = b.we_pay_room_gst_to_property;

  if (section === 'international' || filed === 'none' || filed === 'international') {
    return 'Not filed by us (International)';
  }
  if (
    section === 'property_files_room_gst' ||
    filed === 'property' ||
    filed === 'hotel' ||
    sendToHotel === true
  ) {
    return 'Hotel';
  }
  if (section === 'tabtrips_files_room_gst' || filed === 'tabtrips' || sendToHotel === false) {
    return 'TabTrips';
  }
  return filed || 'Unknown';
};

const remainingBreakdown = (settlement) => {
  const rem = settlement?.remaining_with_tabtrips;
  if (rem == null) return {};
  if (typeof rem !== 'object') {
    return { 'Remaining with TabTrips': rem };
  }
  const bd = rem.breakdown || {};
  return {
    'Remaining with TabTrips': rem.total ?? '',
    'Remaining — commission': bd.tabtrips_commission ?? '',
    'Remaining — GST on commission': bd.gst_on_commission ?? '',
    'Remaining — room GST held': bd.room_gst_held_by_tabtrips ?? '',
    'Remaining — Razorpay expense': bd.razorpay_charge_expense ?? '',
  };
};

/**
 * Full booking row for Excel — all useful packed fields + GST filing columns.
 */
export const flattenPackedBooking = (b) => {
  const rem = remainingBreakdown(b.settlement);
  const filedBy = roomGstFiledByLabel(b);
  const sendToHotel =
    b.we_pay_room_gst_to_property === true ||
    filedBy === 'Hotel';

  return {
    'Booking code': fmt(b.booking_code),
    'Booking ID': fmt(b.booking_id),
    Guest: fmt(b.guest_name),
    Status: fmt(b.booking_status),
    'Check-in': fmt(b.check_in_date),
    'Check-out': fmt(b.check_out_date),
    Nights: fmt(b.total_nights),
    'Created at': fmt(b.created_at),
    'Hotel ID': fmt(b.hotel_id),
    Hotel: fmt(b.hotel_name),
    Location: fmt(b.hotel_location),

    // GST filing — what you asked for
    'GST number': fmt(b.gst_number),
    'Room GST filed by': filedBy,
    'Send room GST to hotel': sendToHotel ? 'YES' : 'NO',
    'GST section': fmt(b.section),
    'Room GST %': fmt(b.room_gst_rate_percent),
    'Room GST amount': num(b.room_gst_amount),

    // Money
    'Base room price': num(b.total_amount),
    Discounted: num(b.discounted_amount),
    'Guest paid (final)': num(b.final_amount_paid),
    Commission: num(b.commission ?? b.commission_and_gst?.commission),
    'GST on commission': num(b.gst_on_commission ?? b.commission_and_gst?.gst_on_commission),
    'Commission + GST display': fmt(b.commission_and_gst?.display),
    Razorpay: num(b.razorpay_charge),
    'Pay to hotel': num(b.final_pay_to_hotel),
    Currency: fmt(b.currency) || 'INR',

    // Payment / settlement
    'Payment method': fmt(b.payment_method),
    'Payment date': fmt(b.payment_date),
    'Settlement status': fmt(b.settlement?.status),
    Settled: b.settlement?.is_settled ? 'YES' : 'NO',
    'Hotel amount (settlement)': num(b.settlement?.hotel_amount),
    'Transfer date': fmt(b.settlement?.transfer_date),
    ...rem,

    // Refund / audit
    'Refund status': fmt(b.refund?.refund_status),
    'Refund ID': fmt(b.refund?.refund_id),
    'Refund fine': num(b.refund?.fine_amount),
    'Audit flag': b.wrong_calculation?.is_wrong ? 'YES' : 'NO',
    'Audit issues': (b.wrong_calculation?.issues || []).join('; '),
  };
};

const MONEY_COLS = [
  'Room GST amount',
  'Base room price',
  'Discounted',
  'Guest paid (final)',
  'Commission',
  'GST on commission',
  'Razorpay',
  'Pay to hotel',
  'Hotel amount (settlement)',
];

const addTotalsRow = (rows) => {
  if (!rows.length) return rows;
  const total = {
    'Booking code': 'TOTAL',
    Guest: `${rows.length} bookings`,
    'Room GST filed by': '',
    'GST number': '',
  };
  MONEY_COLS.forEach((col) => {
    total[col] = rows.reduce((s, r) => s + num(r[col]), 0);
  });
  return [...rows, total];
};

const collectAllBookings = (report) => {
  const sections = report?.booking_sections || {};
  const keys = [
    'tabtrips_files_room_gst',
    'property_files_room_gst',
    'international',
  ];
  const all = [];
  keys.forEach((key) => {
    const list = sections[key]?.bookings || [];
    list.forEach((b) => all.push({ ...b, section: b.section || key }));
  });
  return all.sort(sortByCheckoutThenCode);
};

const getNested = (obj, path) =>
  path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);

const buildSummarySheet = (report, range) => {
  const calc = report?.calculations || {};
  const meta = report?.meta || {};
  const sectionCounts = meta.section_counts || {};
  const all = collectAllBookings(report);
  const tabtripsGst = all.filter((b) => roomGstFiledByLabel(b) === 'TabTrips');
  const hotelGst = all.filter((b) => roomGstFiledByLabel(b) === 'Hotel');
  const intlGst = all.filter((b) => roomGstFiledByLabel(b).startsWith('Not filed'));

  const kpi = (label, path, alt) => {
    let v = getNested(calc, path);
    if (v == null && alt) v = getNested(calc, alt);
    if (v == null) v = calc[path];
    return { Metric: label, Value: v == null ? '' : v };
  };

  return [
    { Metric: 'Report period', Value: `${range.start_date} to ${range.end_date}` },
    { Metric: 'Date field', Value: meta.date_field || '' },
    { Metric: 'Generated at', Value: new Date().toLocaleString('en-IN') },
    { Metric: 'Total bookings (All Bookings sheet)', Value: all.length },
    { Metric: 'Bookings — TabTrips files room GST', Value: tabtripsGst.length },
    { Metric: 'Bookings — Hotel files room GST', Value: hotelGst.length },
    { Metric: 'Bookings — International (not filed by us)', Value: intlGst.length },
    { Metric: 'API section counts TT / Property / Intl', Value: `${sectionCounts.tabtrips_files_room_gst ?? 0} / ${sectionCounts.property_files_room_gst ?? 0} / ${sectionCounts.international ?? 0}` },
    { Metric: '', Value: '' },
    { Metric: '— KPI totals —', Value: '' },
    kpi('Guest collections (final amount paid)', 'sum_final_amount_paid'),
    kpi('Total room GST', 'sum_gst_amount'),
    kpi('Room GST (property / hotel files)', 'gst_filing.room_gst_filed_by_property', 'sum_room_gst_property_files'),
    kpi('Room GST (TabTrips files)', 'gst_filing.room_gst_filed_by_tabtrips', 'sum_room_gst_tabtrips_files'),
    kpi('Commission', 'sum_tabtrips_commission'),
    kpi('GST on commission', 'sum_gst_on_tabtrips_comm'),
    kpi('Razorpay charges', 'razorpay.total_razorpay_charge_all', 'sum_razorpay_charge'),
    kpi('Pay to hotel (sum)', 'sum_final_pay_to_hotel'),
  ];
};

const flattenSettled = (s) => {
  const rem = remainingBreakdown(
    s.remaining_with_tabtrips != null
      ? { remaining_with_tabtrips: s.remaining_with_tabtrips }
      : s.settlement || s
  );
  return {
    'Booking code': fmt(s.booking_code),
    'Booking ID': fmt(s.booking_id),
    Hotel: fmt(s.hotel_name),
    Location: fmt(s.hotel_location),
    'GST number': fmt(s.gst_number),
    'Settlement status': fmt(s.settlement_status || s.status),
    'Transfer date': fmt(s.transfer_date),
    'Razorpay transfer ID': fmt(s.razorpay_transfer_id),
    'Hotel amount sent': num(s.hotel_amount_sent ?? s.hotel_amount),
    ...rem,
  };
};

export const BOOKING_SECTION_KEYS = [
  { key: 'tabtrips_files_room_gst', sheet: 'TT Files Room GST' },
  { key: 'property_files_room_gst', sheet: 'Property Files GST' },
  { key: 'international', sheet: 'International' },
];

const sheetFromRows = (rows) => {
  if (!rows.length) return XLSX.utils.json_to_sheet([{ note: 'No rows' }]);
  return XLSX.utils.json_to_sheet(rows);
};

export const downloadFinanceExcel = (report, sections, range) => {
  const wb = XLSX.utils.book_new();
  const stamp = `${range.start_date}_to_${range.end_date}`;
  const want = sections || DEFAULT_EXCEL_SECTIONS;
  const allBookings = collectAllBookings(report);

  // Primary: full booking dump with GST filing columns
  if (want.booking_sections !== false) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(addTotalsRow(allBookings.map(flattenPackedBooking))),
      'All Bookings'
    );

    BOOKING_SECTION_KEYS.forEach(({ key, sheet }) => {
      const list = [...(report?.booking_sections?.[key]?.bookings || [])].sort(sortByCheckoutThenCode);
      if (!list.length) return;
      XLSX.utils.book_append_sheet(
        wb,
        sheetFromRows(addTotalsRow(list.map(flattenPackedBooking))),
        sheet.slice(0, 31)
      );
    });
  }

  if (want.calculations !== false) {
    XLSX.utils.book_append_sheet(
      wb,
      sheetFromRows(buildSummarySheet(report, range)),
      'Summary'
    );
  }

  if (want.settlements !== false && report?.settlements) {
    const settled = [...(report.settlements.settled_bookings || [])].sort((a, b) =>
      String(a.transfer_date || '').localeCompare(String(b.transfer_date || ''))
    );
    if (settled.length) {
      XLSX.utils.book_append_sheet(
        wb,
        sheetFromRows(settled.map(flattenSettled)),
        'Settled Bookings'
      );
    }
  }

  if (want.wrong_calculations !== false && report?.wrong_calculations) {
    const wrong = (report.wrong_calculations.bookings || [])
      .filter((b) => b.wrong_calculation?.is_wrong || b.is_wrong)
      .sort(sortByCheckoutThenCode);
    if (wrong.length) {
      XLSX.utils.book_append_sheet(
        wb,
        sheetFromRows(wrong.map(flattenPackedBooking)),
        'Wrong Calculations'
      );
    }
  }

  if (wb.SheetNames.length === 0) {
    XLSX.utils.book_append_sheet(wb, sheetFromRows([{ note: 'No data' }]), 'Empty');
  }

  XLSX.writeFile(wb, `TabTrips_Finance_GST_${stamp}.xlsx`);
};

export const DEFAULT_EXCEL_SECTIONS = {
  booking_sections: true,
  calculations: true,
  settlements: true,
  wrong_calculations: true,
};

export const DEFAULT_INCLUDE_SECTIONS = {
  booking_sections: true,
  calculations: true,
  settlements: true,
  wrong_calculations_report: true,
  monthly_profit: true,
  cancellation_loss: true,
  gst_claim_on_cancellations: true,
  checkout_in_period: false,
  future_bookings: false,
};

export const EXCEL_SECTION_LABELS = {
  booking_sections: 'All bookings (full details + GST filing columns)',
  calculations: 'Summary totals',
  settlements: 'Settled bookings',
  wrong_calculations: 'Wrong calculation audit',
};
